import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import { uploadFileToServer, downloadFileFromServer, deleteFileFromServer } from '../services/fileUpload'
import { canUploadFile, recordFileUpload, recordFileDeletion } from '../services/storageManagement'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import Select from './Select'
import { FileText, Upload, X, Download, Trash2, Plus, File } from 'lucide-react'
import { formatDate } from '../utils/helpers'

const DOCUMENT_CATEGORIES = [
  { value: 'medical_record', label: 'Medical Record' },
  { value: 'consent_form', label: 'Consent Form' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
]

const ClientDocuments = ({ clientId }) => {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
  })
  const fileInputRef = useRef(null)
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  useEffect(() => {
    if (clientId && organizationId) {
      fetchDocuments()
    }
  }, [clientId, organizationId])

  const fetchDocuments = async () => {
    if (!clientId || !organizationId) return
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('client_documents')
        .select('*, uploaded_by_user:uploaded_by(email, raw_user_meta_data)')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setDocuments(data || [])
    } catch (error) {
      console.error('Error fetching documents:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type - only PDF and images allowed
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ]
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp']
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase()

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert('Invalid file type. Only PDF and image files (JPG, PNG, GIF, WEBP) are allowed.')
      e.target.value = '' // Clear the input
      setSelectedFile(null)
      return
    }

    setSelectedFile(file)
    if (!formData.name) {
      setFormData({ ...formData, name: file.name })
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    if (!selectedFile || !clientId || !organizationId) return

    try {
      setUploading(true)
      const { data: { user } } = await supabase.auth.getUser()

      // Validate file type again before upload (extra safety check)
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
      ]
      const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp']
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase()

      if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExtension)) {
        alert('Invalid file type. Only PDF and image files (JPG, PNG, GIF, WEBP) are allowed.')
        setUploading(false)
        return
      }

      // Check storage limits before uploading
      const canUpload = await canUploadFile(organizationId, selectedFile.size)
      if (!canUpload.canUpload) {
        alert(canUpload.reason || 'Cannot upload file. Storage limit reached.')
        setUploading(false)
        return
      }

      // Upload file to hPanel server (not Supabase Storage)
      const uploadResult = await uploadFileToServer(
        selectedFile,
        organizationId,
        clientId,
        formData.category
      )

      if (!uploadResult.success) {
        throw new Error('File upload failed')
      }

      // Record file upload in storage tracking
      await recordFileUpload(
        organizationId,
        uploadResult.filePath,
        selectedFile.size,
        selectedFile.type,
        formData.category,
        user?.id
      )

      // Create document record in Supabase (metadata only - no file)
      const { error: docError } = await supabase.from('client_documents').insert({
        organization_id: organizationId,
        client_id: clientId,
        name: formData.name || selectedFile.name,
        description: formData.description || null,
        file_path: uploadResult.filePath, // Path on hPanel server
        file_url: uploadResult.fileUrl, // URL to access file on hPanel server
        file_type: selectedFile.type,
        file_size: selectedFile.size,
        category: formData.category,
        uploaded_by: user?.id,
      })

      if (docError) throw docError

      // Reset form
      setFormData({ name: '', description: '', category: 'other' })
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setShowForm(false)
      fetchDocuments()
    } catch (error) {
      console.error('Error uploading document:', error)
      // Show more detailed error message
      const errorMessage = error.message || 'Failed to upload document'
      alert(`Upload Error: ${errorMessage}\n\nIf this persists, please check:\n- Your internet connection\n- API endpoint is accessible\n- File size is under 10MB`)
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (document) => {
    try {
      // Download file from hPanel server
      await downloadFileFromServer(document.file_path || document.file_url, document.name)
    } catch (error) {
      console.error('Error downloading document:', error)
      alert(error.message || 'Failed to download document')
    }
  }

  const handleDelete = async (docId, filePath, fileUrl) => {
    if (!confirm('Delete this document? This cannot be undone.')) return
    try {
      // Get document info before deletion to track storage
      const doc = documents.find((d) => d.id === docId)
      
      // Delete file from hPanel server
      const deletePath = filePath || fileUrl
      if (deletePath) {
        try {
          await deleteFileFromServer(deletePath)
        } catch (storageError) {
          console.error('Error deleting file from server:', storageError)
          // Continue with database deletion even if file deletion fails
        }
      }

      // Record file deletion in storage tracking
      if (doc && organizationId) {
        await recordFileDeletion(organizationId, deletePath)
      }

      // Delete record from Supabase (metadata only)
      const { error } = await supabase.from('client_documents').delete().eq('id', docId)
      if (error) throw error

      fetchDocuments()
    } catch (error) {
      console.error('Error deleting document:', error)
      alert(error.message || 'Failed to delete document')
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return FileText
    if (fileType?.includes('image')) return File
    return File
  }

  if (loading) return <div className="text-sm text-gray-500">Loading documents...</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText size={20} />
          Documents
        </h3>
        {canWrite('clients') && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1"
          >
            <Plus size={16} />
            Upload Document
          </Button>
        )}
      </div>

      {/* Upload Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card variant="soft" className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Upload Document</h4>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleUpload} className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    File <span className="text-xs text-gray-500 font-normal">(PDF or image only)</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,application/pdf,image/jpeg,image/png,image/gif,image/webp"
                    />
                    <label
                      htmlFor="file-upload"
                      className="flex-1 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload size={18} />
                      <span className="text-sm text-gray-600">
                        {selectedFile ? selectedFile.name : 'Choose file (PDF or image)...'}
                      </span>
                    </label>
                  </div>
                </div>
                <Input
                  label="Document Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Name for this document"
                  required
                />
                <Select
                  label="Category"
                  options={DOCUMENT_CATEGORIES}
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
                <Textarea
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button type="submit" disabled={!selectedFile || uploading} className="flex-1">
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Documents List */}
      {documents.length === 0 ? (
        <Card variant="soft" className="p-6 text-center">
          <FileText size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No documents uploaded yet</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {documents.map((doc) => {
            const FileIcon = getFileIcon(doc.file_type)
            return (
              <Card key={doc.id} variant="soft" className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary-100 text-primary-600">
                    <FileIcon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 truncate mb-1">{doc.name}</h4>
                    {doc.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{doc.description}</p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>{formatDate(doc.created_at)}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleDownload(doc)}
                        className="flex items-center gap-1"
                      >
                        <Download size={14} />
                        Download
                      </Button>
                      {canWrite('clients') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleDelete(doc.id, doc.file_path, doc.file_url)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ClientDocuments

